import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Skeleton, Text } from '../../../../components';
import { CASINO_SETTINGS, COLORS, DELTA_PILL, MAILBOX_SLOT } from '../../../../theme/tokens';
import type { MailboxRecord } from '../../../../mock-data/mailbox-records';
import {
  formatMailboxStorage,
  mailboxShowsStatusPill,
  mailboxStorageRatio,
  mailboxStorageTone,
} from '../../state';
import { mailboxErrorText } from '../../errors';
import { readyUsage, type MailboxUsageRead } from '../../useMailboxUsage';
import {
  Tile,
  TopGroup,
  Head,
  Address,
  HeadSlot,
  StatusPill,
  SubLine,
  Bottom,
  Storage,
  Figures,
  Figure,
  StorageLabel,
  StorageValue,
  Bar,
  BarFill,
  BarSlot,
  Footer,
  FooterSlot,
  FooterLink,
  Chevron,
} from './MailboxTile.styles';

export type MailboxTileProps = {
  mailbox: MailboxRecord;
  /** The mailbox's usage, read separately from the list and lazily (see `useMailboxUsage`). */
  usage: MailboxUsageRead;
  /** The tile has scrolled into view while its usage is still unread: ask for it. Called at most
   *  once per observation; the page's cache makes a repeat a no-op. */
  onVisible?: (mailbox: MailboxRecord) => void;
  /** Webmail: signs in to webmail in a new tab. Must be handled synchronously in the click (the
   *  handler opens the tab before it awaits anything; see `webmail.ts`). */
  onWebmail?: (mailbox: MailboxRecord) => void;
  /** Setup Mailbox: opens the mailbox page (its own route). */
  onServerSettings?: (email: string) => void;
  /** The domain's setup failed: the tile still says what the mailbox is, and offers nothing to
   *  press (no Webmail, no Setup Mailbox). */
  inert?: boolean;
  /** support@ while its DOMAIN is being set up (see `mailboxShowsPending`): a Pending pill in the head
   *  slot instead of the slot word. A Pending card is always WAITING too (see `awaitingDomain`).
   *  Pending describes the domain, not the mailbox: it exists and its usage is its own, as on any
   *  tile; only receiving waits for the domain. */
  pending?: boolean;
  /** The domain is still being PROVISIONED (see `mailboxDomainAwaitingSetup`), for ANY mailbox on it:
   *  Webmail stays on the card but is DISABLED (its handler refuses too), Setup Mailbox only opens the
   *  mailbox's page (which holds the client setup back itself), and a line under the address says
   *  why. The slot word stays. See the WAITING note in the body. */
  awaitingDomain?: boolean;
};

/** A status pill: a word and its colour pair, and nothing else (no glyph; the colour carries the
 *  urgency, and an icon only crowds a 24px pill). */
type Pill = { label: string; bg: string; fg: string };

/**
 * One mailbox tile in the Mailbox grid: TWO blocks pinned to the two edges, identity at the top,
 * everything else at the bottom, so every card in a row lines up whatever its content. "The action
 * carries the state": there is no `Active` badge and no kebab.
 *
 *   top           the address, and ONE pill top right, a word and its colour with no glyph: a
 *                 status (Deleted, Suspended, Pending) when one applies, else the slot word (Free /
 *                 Paid). Under the address, on a WAITING card, the line saying why it cannot be set
 *                 up yet.
 *   bottom        Storage and Messages on one row (read lazily, when the tile scrolls into view: a
 *                 skeleton until it answers, never a 0 or a 0% bar; a dash when there is nothing to
 *                 read), the storage bar (warning-toned past the threshold; no bar when no ceiling
 *                 is reported), then the footer: Setup Mailbox left, Webmail right (present but
 *                 disabled on a WAITING card). No tile offers Delete: it lives on the mailbox page.
 *
 * The price is not on the tile: the `Paid` pill is all it needs. The figure (the service's, for the
 * NEXT mailbox) lives on the add card, the create form and the checkout, never on an existing one.
 */
export const MailboxTile = ({
  mailbox,
  usage,
  onVisible,
  onWebmail,
  onServerSettings,
  inert = false,
  pending = false,
  awaitingDomain = false,
}: MailboxTileProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  // Deleted at the provider: the row is still listed, but it is inert (no restore exists in the
  // service), so it reads muted and offers nothing to press.
  const deleted = mailbox.status === 'deleted';
  const muted = deleted;
  const address = mailbox.email;
  const read = readyUsage(usage);
  const usageLoading = usage.status === 'loading';

  // THE LAZY USAGE READ: while this tile's usage is unread, watch for the tile entering the
  // viewport and ask then, so a grid of forty mailboxes reads the handful on screen. Without an
  // IntersectionObserver (an old engine) it asks at once rather than never.
  const tileRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!usageLoading || !onVisible) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      onVisible(mailbox);
      return undefined;
    }
    const el = tileRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      onVisible(mailbox);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [usageLoading, onVisible, mailbox]);

  // THE PILL SLOT (top right): ONE pill, and a status wins over the slot word (Free / Paid): Deleted
  // outranks everything, then the provider's Suspended, then Pending.
  const statusPill: Pill | null = React.useMemo(() => {
    if (deleted) {
      return { label: t('Deleted', { context: 'mailbox' }), bg: DELTA_PILL.negative.bg, fg: DELTA_PILL.negative.fg };
    }
    if (mailbox.status === 'suspended') return { label: t('Suspended'), bg: COLORS.surfaceCard, fg: COLORS.textMuted };
    // The domain is still being set up: the amber "locked/pending" pair, ahead of the slot word.
    if (pending) return { label: t('Pending'), bg: CASINO_SETTINGS.lockBg, fg: CASINO_SETTINGS.lockFg };
    return null;
  }, [deleted, mailbox.status, pending, t]);
  // Webmail, bottom right, where the mailbox can be signed in to: not deleted, not a suspended seat,
  // not on a failed domain.
  const webmail = !muted && !inert && !mailboxShowsStatusPill(mailbox);

  // WAITING (a Pending card, or any mailbox on a domain still being provisioned): Webmail stays on
  // the card (the row keeps its shape when the domain goes ready) but is DISABLED, and its handler
  // refuses too. Setup Mailbox stays ENABLED but only opens the mailbox's page, which holds the
  // client guide, the server table and Webmail back itself while the domain waits, and keeps the
  // trash reachable: a paid mailbox created by mistake is already charged and must not be stuck for
  // hours. This DEPARTS from frontend-integration-main.md section 3 ("`ready: false` does not mean
  // unusable ... do not hide the credentials") on purpose: a mailbox that sends but does not yet
  // receive, set up in Thunderbird or Outlook during the wait, receives nothing and reads as our
  // defect, not as DNS still propagating. That happened in testing. A decision, not a bug to "fix".
  // A DELETED mailbox never waits: it will never be set up, so it says nothing about it.
  const waiting = (pending || awaitingDomain) && !deleted;
  const onOpenWebmail = React.useCallback(() => {
    if (waiting) return;
    onWebmail?.(mailbox);
  }, [waiting, onWebmail, mailbox]);
  const onSettings = React.useCallback(() => onServerSettings?.(mailbox.email), [onServerSettings, mailbox.email]);

  return (
    <Tile
      ref={tileRef}
      $muted={muted}
      // A Pending card keeps its own test id so the grid's cards and its Pending one count apart.
      data-testid={pending ? 'mailbox-pending-tile' : 'mailbox-mailbox-tile'}
      data-status={mailbox.status}
    >
      <TopGroup>
        <Head>
          <Address $muted={muted} title={address}>
            {address}
          </Address>
          <HeadSlot>
            {statusPill ? (
              <StatusPill $bg={statusPill.bg} $fg={statusPill.fg} data-testid="mailbox-status-pill">
                {statusPill.label}
              </StatusPill>
            ) : (
              // The SLOT word: what the mailbox IS, as the service's two values say it. Both free ones
              // (the brand's system mailbox and the owner's first) read "Free"; `role` tells them
              // apart where that matters.
              <StatusPill
                $bg={mailbox.billing === 'paid' ? MAILBOX_SLOT.paid.bg : MAILBOX_SLOT.free.bg}
                $fg={mailbox.billing === 'paid' ? MAILBOX_SLOT.paid.fg : MAILBOX_SLOT.free.fg}
                data-testid="mailbox-slot-pill"
                data-slot={mailbox.billing}
              >
                {mailbox.billing === 'paid' ? <Text text="Paid" tag="span" /> : <Text text="Free" tag="span" />}
              </StatusPill>
            )}
          </HeadSlot>
        </Head>

        {waiting ? (
          // Says WHY Webmail is disabled (and why the page Setup Mailbox opens holds the client setup
          // back): a disabled control with no stated reason is its own bug.
          <span data-testid="mailbox-pending-line">
            <SubLine
              text="This mailbox can be set up once its domain is ready to receive mail."
              tag="span"
              $tone="pending"
              $muted={false}
            />
          </span>
        ) : null}
      </TopGroup>

      {/* THE BOTTOM BLOCK: the figures, the bar and the footer, pinned to the tile's bottom edge. */}
      <Bottom>
        <Storage>
          <Figures>
            <Figure>
              <StorageLabel text="Storage" tag="span" $muted={muted} />
              {/* A failed read keeps the service's own message on the dash: the tile has one line
                  for the figure, so the operator's explanation is a hover away rather than lost. */}
              <StorageValue
                $muted={muted}
                data-testid="mailbox-storage-value"
                title={usage.status === 'failed' ? mailboxErrorText(usage.error, t, '') || undefined : undefined}
              >
                {usageLoading ? (
                  <Skeleton width={96} height={14} />
                ) : read ? (
                  formatMailboxStorage(read.bytesUsed, read.bytesMax, t)
                ) : (
                  // No usage to read, or the read failed: a dash, the area's no-value marker. Not
                  // "0 KB": nothing was read.
                  '–'
                )}
              </StorageValue>
            </Figure>
            {/* Messages, from the same usage read (no extra call), by Storage's own rules: a skeleton
                while it loads, a dash when there is nothing to read, never 0 for an unknown (0 is an
                answer). `messagesMax` is not shown here: the mailbox page is where a cap surfaces. */}
            <Figure $end>
              <StorageLabel text="Messages" tag="span" $muted={muted} />
              <StorageValue $muted={muted} data-testid="mailbox-messages-value">
                {usageLoading ? <Skeleton width={40} height={14} /> : read ? read.messagesUsed.toLocaleString(i18n.language) : '–'}
              </StorageValue>
            </Figure>
          </Figures>
          {usageLoading ? (
            // NOT ANSWERED YET: a skeleton in the bar's slot, never an empty bar. A 0% bar is an
            // answer ("nothing stored"); this is the absence of one. The skeleton is rounded and
            // sheens, the bar is flat and still, so the two never read as the same thing.
            <BarSlot data-testid="mailbox-storage-loading" aria-busy="true">
              <Skeleton block height={12} />
            </BarSlot>
          ) : read && read.bytesMax !== undefined ? (
            <Bar
              $muted={muted}
              role="progressbar"
              aria-label={t('Storage')}
              aria-valuemin={0}
              aria-valuemax={read.bytesMax}
              aria-valuenow={Math.min(read.bytesUsed, read.bytesMax)}
            >
              <BarFill
                $ratio={mailboxStorageRatio(read.bytesUsed, read.bytesMax)}
                $tone={mailboxStorageTone(read.bytesUsed, read.bytesMax)}
              />
            </Bar>
          ) : (
            // ABSENT `bytesMax` MEANS UNKNOWN, NOT ZERO: the provider reported no ceiling, so there is
            // nothing to measure against and no bar (a bar here would claim a fill it cannot know).
            // Same slot, same height, when there is no usage at all or the read failed.
            <BarSlot data-testid="mailbox-storage-no-bar" />
          )}
        </Storage>

        <Footer>
          <FooterSlot>
            {deleted || inert ? null : (
              // "Setup Mailbox", DIVERGING from the label Figma 46:877 draws here on purpose: a product
              // call. Do not "fix" it back to Figma's wording on the next diff.
              <FooterLink type="button" onClick={onSettings}>
                <Text text="Setup Mailbox" tag="span" />
                <Chevron>
                  <Icon name="chevron-down" size={16} color="currentColor" />
                </Chevron>
              </FooterLink>
            )}
          </FooterSlot>
          <FooterSlot>
            {webmail ? (
              // A deleted or suspended mailbox never shows it; the SSO call would answer 409.
              <FooterLink type="button" onClick={onOpenWebmail} disabled={waiting}>
                <Text text="Webmail" tag="span" />
                <Icon name="external-link" size={16} color="currentColor" />
              </FooterLink>
            ) : null}
          </FooterSlot>
        </Footer>
      </Bottom>
    </Tile>
  );
};

export default MailboxTile;
